import * as Guacamole from '@glokon/guacamole-common-js';
import { useEffect, useRef, useState } from 'react';
import { GUACAMOLE_CLIENT_CONTAINER_ID } from '../../constants/vms';
import { useGuacamoleConnection } from './use-guacamole-connection';
export const GuacamoleClient = ({
  remoteUrl,
  onConnectionFailed
}: {
  remoteUrl: string;
  onConnectionFailed?: () => void;
}) => {
  const displayRef = useRef<HTMLDivElement>(null);
  const {
    guacClient,
    isConnected,
    isConnecting,
    websocketFailed,
    error
  } = useGuacamoleConnection(remoteUrl);
  const [isDisplayReady, setIsDisplayReady] = useState<boolean>(false);
  const mouseRef = useRef<Guacamole.Mouse | null>(null);
  const keyboardRef = useRef<Guacamole.Keyboard | null>(null);
  const clipboardSyncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initResizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentDisplayElementRef = useRef<HTMLElement | null>(null);
  const cachedScaleRef = useRef<number>(1);
  const cachedMarginLeftRef = useRef<number>(0);
  const cachedMarginTopRef = useRef<number>(0);
  const RESIZE_THRESHOLD_PX = 20;

  // biome-ignore lint/correctness/useExhaustiveDependencies: isDisplayReady is intentionally excluded to prevent effect retrigger when display becomes ready
  useEffect(() => {
    if (!isConnected || !guacClient || !displayRef.current) {
      setIsDisplayReady(false);
      return;
    }

    // Clean up any existing display elements first
    if (displayRef.current) {
      while (displayRef.current.firstChild) {
        displayRef.current.removeChild(displayRef.current.firstChild);
      }
    }

    // Add new display to DOM
    const displayElement = guacClient.getDisplay().getElement();
    displayRef.current.appendChild(displayElement);
    currentDisplayElementRef.current = displayElement;
    if (displayRef.current) {
      displayRef.current.tabIndex = 0;
      displayRef.current.focus();
    }
    const updateCachedTransformValues = (scale: number, offsetX: number, offsetY: number) => {
      cachedScaleRef.current = scale;
      cachedMarginLeftRef.current = offsetX;
      cachedMarginTopRef.current = offsetY;
    };
    updateCachedTransformValues(1, 0, 0);

    // Browsers with clipboard polling issues: use event-driven approach
    // => Causing limitations, since the events are not fired when the page is not active
    // => It's not possible to sync the clipboard when the page is not active.
    // Other browsers: use polling every 300ms
    const CLIPBOARD_POLLING_BLACKLIST = ['firefox'];
    const userAgent = navigator.userAgent.toLowerCase();
    const shouldUseEventDriven = CLIPBOARD_POLLING_BLACKLIST.some(browser => userAgent.includes(browser));
    let lastClipboardContent = '';
    const syncClipboardToVM = async () => {
      if (isConnected && guacClient && displayRef.current) {
        try {
          const text = await navigator.clipboard.readText();
          if (text && text !== lastClipboardContent && text.length < 1000) {
            lastClipboardContent = text;
            const writer = new Guacamole.StringWriter(guacClient.createClipboardStream('text/plain'));
            writer.sendText(text);
            writer.sendEnd();
          }
        } catch (error) {}
      }
    };
    let handleClipboardChange: (() => void) | null = null;
    if (shouldUseEventDriven) {
      handleClipboardChange = () => {
        setTimeout(() => syncClipboardToVM(), 100);
      };
      window.addEventListener('copy', handleClipboardChange);
      window.addEventListener('cut', handleClipboardChange);
    } else {
      clipboardSyncIntervalRef.current = setInterval(() => {
        syncClipboardToVM();
      }, 300);
    }

    // Set up resize handling
    const handleResize = () => {
      if (!guacClient || !displayRef.current || !isConnected) {
        return;
      }
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        if (!guacClient || !displayRef.current) return;
        const containerRect = displayRef.current.getBoundingClientRect();
        if (containerRect.width <= 0 || containerRect.height <= 0) {
          return;
        }
        const pixelDensity = window.devicePixelRatio || 1;
        const optimalWidth = Math.floor(containerRect.width * pixelDensity);
        const optimalHeight = Math.floor(containerRect.height * pixelDensity);
        const display = guacClient.getDisplay();
        const currentWidth = display.getWidth();
        const currentHeight = display.getHeight();
        const widthDiff = Math.abs(currentWidth - optimalWidth);
        const heightDiff = Math.abs(currentHeight - optimalHeight);
        if (widthDiff > RESIZE_THRESHOLD_PX || heightDiff > RESIZE_THRESHOLD_PX) {
          guacClient.sendSize(optimalWidth, optimalHeight);
        }
      }, 250);
    };

    // Set up display styling
    displayElement.style.display = 'block';
    displayElement.style.pointerEvents = 'auto';

    // Set up mouse support on the new display element
    mouseRef.current = new Guacamole.Mouse(displayElement);

    // biome-ignore lint/suspicious/noExplicitAny: Guacamole types are not fully typed
    const handleMouseEvent = (state: any) => {
      if (guacClient && isConnected) {
        const scale = cachedScaleRef.current;
        const marginLeft = cachedMarginLeftRef.current;
        const marginTop = cachedMarginTopRef.current;
        const adjustedState = {
          ...state
        };
        adjustedState.x = (state.x - marginLeft) / scale;
        adjustedState.y = (state.y - marginTop) / scale;
        guacClient.sendMouseState(adjustedState);
      }
    };

    // biome-ignore lint/suspicious/noExplicitAny: Guacamole types are incomplete
    (mouseRef.current as any).onmousedown = handleMouseEvent;
    // biome-ignore lint/suspicious/noExplicitAny: Guacamole types are incomplete
    (mouseRef.current as any).onmousemove = handleMouseEvent;
    // biome-ignore lint/suspicious/noExplicitAny: Guacamole types are incomplete
    (mouseRef.current as any).onmouseup = handleMouseEvent;

    // Set up keyboard support
    keyboardRef.current = new Guacamole.Keyboard(displayRef.current);
    keyboardRef.current.onkeydown = (keysym: number) => {
      if (guacClient && isConnected) {
        guacClient.sendKeyEvent(1, keysym);
      }
    };
    keyboardRef.current.onkeyup = (keysym: number) => {
      if (guacClient && isConnected) {
        guacClient.sendKeyEvent(0, keysym);
      }
    };

    // Set up click handler to ensure focus
    const handleClick = () => {
      if (displayRef.current) {
        displayRef.current.focus();
      }
    };
    displayRef.current.addEventListener('click', handleClick);

    // Set up clipboard support - VM to local sync (re-established after reconnection)
    guacClient.onclipboard = (stream: Guacamole.InputStream) => {
      const reader = new Guacamole.StringReader(stream);
      reader.ontext = (text: string) => {
        // Validate content before writing to local clipboard
        if (text && text.trim().length > 0 && !text.includes('console.log') && text.length < 1000) {
          lastClipboardContent = text; // Update tracking to prevent echo
          navigator.clipboard.writeText(text).catch(error => {
            console.error('📋 Failed to write VM clipboard to local clipboard:', error);
          });
        }
      };
      // Note: StringReader might not have onerror property, handled by try-catch above
    };

    // Set up display resize handler
    const display = guacClient.getDisplay();
    display.onresize = (width: number, height: number) => {
      if (displayRef.current) {
        const containerRect = displayRef.current.getBoundingClientRect();
        const guacDisplayElement = display.getElement();
        if (guacDisplayElement) {
          const scaleX = containerRect.width / width;
          const scaleY = containerRect.height / height;
          const scale = Math.min(scaleX, scaleY, 1);
          guacDisplayElement.style.width = `${width}px`;
          guacDisplayElement.style.height = `${height}px`;
          guacDisplayElement.style.transform = `scale(${scale})`;
          guacDisplayElement.style.transformOrigin = 'top left';
          const scaledWidth = width * scale;
          const scaledHeight = height * scale;
          const offsetX = (containerRect.width - scaledWidth) / 2;
          const offsetY = (containerRect.height - scaledHeight) / 2;
          guacDisplayElement.style.marginLeft = `${offsetX}px`;
          guacDisplayElement.style.marginTop = `${offsetY}px`;
          updateCachedTransformValues(scale, offsetX, offsetY);

          // Display is ready
          if (!isDisplayReady) {
            setIsDisplayReady(true);
          }
        }
      }
    };

    // Set up resize observers
    if ('ResizeObserver' in window) {
      resizeObserverRef.current = new ResizeObserver(handleResize);
      resizeObserverRef.current.observe(displayRef.current);
    }
    window.addEventListener('resize', handleResize);
    const checkClipboardPermissions = async () => {
      try {
        if (navigator.permissions) {
          const permission = await navigator.permissions.query({
            name: 'clipboard-read' as PermissionName
          });
          if (permission.state === 'denied') {
            console.warn('📋 Clipboard read permission denied - copy/paste may not work properly');
          }
        }
      } catch (error) {}
    };
    checkClipboardPermissions();
    initResizeTimeoutRef.current = setTimeout(() => {
      if (isConnected) {
        handleResize();
      }
    }, 500);
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = null;
      }
      if (initResizeTimeoutRef.current) {
        clearTimeout(initResizeTimeoutRef.current);
        initResizeTimeoutRef.current = null;
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (clipboardSyncIntervalRef.current) {
        clearInterval(clipboardSyncIntervalRef.current);
        clipboardSyncIntervalRef.current = null;
      }
      if (handleClipboardChange) {
        window.removeEventListener('copy', handleClipboardChange);
        window.removeEventListener('cut', handleClipboardChange);
      }
      window.removeEventListener('resize', handleResize);
      if (displayRef.current) {
        displayRef.current.removeEventListener('click', handleClick);
      }
      if (mouseRef.current) {
        mouseRef.current = null;
      }
      if (keyboardRef.current) {
        keyboardRef.current.onkeydown = null;
        keyboardRef.current.onkeyup = null;
        keyboardRef.current = null;
      }
      currentDisplayElementRef.current = null;
    };
  }, [isConnected, guacClient]);
  useEffect(() => {
    if (displayRef.current) {
      displayRef.current.style.cursor = isDisplayReady ? 'none' : 'default';
    }
  }, [isDisplayReady]);
  useEffect(() => {
    if (websocketFailed && onConnectionFailed) {
      onConnectionFailed();
    }
  }, [websocketFailed, onConnectionFailed]);
  if (isConnecting && !isConnected) {
    return <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      backgroundColor: '#1a1a1a',
      color: 'white'
    }}>
        <div style={{
        marginBottom: '16px',
        fontSize: '18px',
        fontWeight: '500'
      }}>Connecting to remote machine...</div>
      </div>;
  }
  if (websocketFailed && !onConnectionFailed) {
    return <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      backgroundColor: '#1a1a1a',
      color: 'white'
    }}>
        <div style={{
        marginBottom: '16px',
        fontSize: '18px',
        fontWeight: '500',
        color: '#ff6b6b'
      }}>
          Connection Failed
        </div>
        <div style={{
        fontSize: '14px',
        opacity: 0.7,
        textAlign: 'center',
        maxWidth: '400px'
      }}>
          {error || 'Unable to connect to the remote machine.'} The machine will fall back to iframe mode.
        </div>
      </div>;
  }
  return <div id={GUACAMOLE_CLIENT_CONTAINER_ID} ref={displayRef} className={`guacamole-client-container${isConnected ? ' guacamole-connected' : ''}`} role="application" aria-label="Virtual Machine Display" style={{
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    outline: 'none'
  }} data-sentry-component="GuacamoleClient" data-sentry-source-file="guacamole-client.tsx" />;
};